"use client";
import cn from "@/utils/cn";
import TextComponent from "@/app/_components/TextComponent";
import { GridIcon, ListIcon } from "@/assets/svg-icons/svg-icons";
import ReusableDropdown from "@/app/(dashboard)/dashboard/_components/ReusableDropdown";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { motion } from "framer-motion"; // <-- Import motion

export type ViewType = "card" | "list";
export type FilterType = "all" | "article" | "feature";

interface SearchFilterBarProps {
  selectedFilter: FilterType;
  onFilterChange: (filter: FilterType) => void;
  viewType: ViewType;
  onViewTypeChange: (view: ViewType) => void;
  className?: string;
}

export default function SearchFilterBar({
  selectedFilter,
  onFilterChange,
  viewType,
  onViewTypeChange,
  className,
}: SearchFilterBarProps) {
  const filterByTypes = [
    { value: "all", label: "All" },
    { value: "article", label: "Articles" },
    { value: "feature", label: "Features" },
  ];
  const dropdownItems = filterByTypes.map((item) => ({
    id: item.value,
    name: item.label,
  }));

  return (
    <div className={cn("w-full space-y-4 bg-white pt-10", className)}>
      <div className="flex flex-row items-center justify-between gap-4 pb-3 md:pb-0">
        <div className="w-full sm:w-auto">
          {/* Mobile Dropdown (unchanged) */}
          <div className="md:hidden">
            <ReusableDropdown
              title="Filter By"
              selectedItemId={selectedFilter}
              items={dropdownItems}
              onSelect={(id) => onFilterChange(id as FilterType)}
              IconOpen={FaChevronDown}
              IconClosed={FaChevronUp}
              variant="simple"
              buttonClassName="w-full !justify-between"
              modalPosition="bottom-left"
              className="w-fit"
            />
          </div>

          {/* ---- CORRECTED DESKTOP FILTERS WITH FRAMER MOTION ---- */}
          <div className="hidden space-x-2 md:flex xl:space-x-5">
            {filterByTypes.map((item) => {
              const isSelected = selectedFilter === item.value;
              return (
                // 1. Clickable wrapper for each filter option
                <div
                  key={item.value}
                  onClick={() => onFilterChange(item.value as FilterType)}
                  // 2. Set position to relative to contain the animated background
                  className="relative cursor-pointer"
                >
                  <TextComponent
                    type="h4"
                    className={cn(
                      // Base styles for layout and centering
                      "flex h-10 items-center justify-center px-2 text-center transition-colors duration-300 xl:h-[60px] xl:min-w-[78px] xl:px-4 min-w-[86px]",
                      // Make text appear above the background
                      "relative z-10",
                      // Text color and weight changes
                      isSelected
                        ? "font-medium text-kd-lte-blue"
                        : "font-normal text-kd-secondary-1 hover:text-kd-lte-blue",
                    )}
                  >
                    {item.label}
                  </TextComponent>

                  {/* 3. The conditionally rendered, animated background */}
                  {isSelected && (
                    <motion.div
                      // This ID tells Framer Motion to animate this element
                      // between the different filter options.
                      layoutId="active-filter-background"
                      // Position it absolutely to fill the parent wrapper
                      className="absolute inset-0 z-0 rounded-t-[12px] bg-[#FAFAFA] xl:rounded-t-[16px]"
                      // Customize the animation spring for a nice effect
                      transition={{
                        type: "spring",
                        stiffness: 350,
                        damping: 30,
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* View Toggle (unchanged) */}
        <div className="flex items-center gap-2">
          {/* ... view toggle buttons ... */}
          <div className="flex h-[26px] items-center justify-center space-x-1 rounded-md border border-kd-secondary-6 bg-white p-1.5 text-[7.2px] font-medium xl:h-[44px] xl:rounded-[10px] xl:text-[12px]">
            <button
              onClick={() => onViewTypeChange("card")}
              className={cn(
                "flex items-center gap-2 rounded-[4px] xl:rounded-md px-[5px] xl:px-2 xl:py-1.5 py-[3px] h-[16px] xl:h-[26px] font-medium transition-colors",
                viewType === "card"
                  ? "bg-kd-lte-blue text-white"
                  : "text-gray-700 hover:bg-gray-100",
              )}
            >
              <GridIcon
                color={viewType === "card" ? "white" : "#444A6D"}
                className="size-3 xl:size-5"
              />
              Grid
            </button>
            <button
              onClick={() => onViewTypeChange("list")}
              className={cn(
                "flex items-center gap-2 rounded-[4px] xl:rounded-md px-[5px] xl:px-2 xl:py-1.5 py-[3px] h-[16px] xl:h-[26px] font-medium transition-colors",
                viewType === "list"
                  ? "bg-kd-lte-blue text-white"
                  : "text-gray-700 hover:bg-gray-100",
              )}
            >
              <ListIcon
                color={viewType === "list" ? "white" : "#444A6D"}
                className="size-3 xl:size-5"
              />
              List
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
